import { RichTextAdvancedRender } from '@framework/engine/inputs/rich-text-advanced/render/RichTextAdvancedRender.tsx';
import { type IWidgetComponentProps } from '@framework/engine/schema/widgets/IWidgetType.ts';
import { getWidgetProps } from '@framework/engine/schema/widgets/repository/inputs/common/util/getWidgetProps.ts';
import { ConfigFormItem } from '@framework/engine/ui/form/ConfigFormItem.tsx';
import { DraggerInput } from '@framework/form/inputs/upload/DraggerInput.tsx';
import { UploadInput } from '@framework/form/inputs/upload/UploadInput.tsx';
import { useWatch } from 'react-hook-form';
import { useFillableSubmissionContext } from '../../../FillableSubmissionContext.tsx';
import {
  getSectionIdFromFieldName,
  useUploadWidgetTitle,
} from '../hooks/useUploadWidget.tsx';
import { useUploadWidgetFieldName } from '../hooks/useUploadWidgetFieldName.ts';
import { useUploadWidgetHelpText } from '../hooks/useUploadWidgetHelpText.ts';
import { type IUploadWidget, UploadWidgetKind } from '../IUploadWidget.ts';
import { UploadWidgetFileList } from './UploadWidgetFileList.tsx';

export interface IUploadWidgetReadonlyComponentInputProps
  extends IWidgetComponentProps<IUploadWidget> {
  widgetName: string;
  publicUpload?: boolean;
}

export function UploadWidgetReadonlyComponentInput(
  props: IUploadWidgetReadonlyComponentInputProps,
) {
  const { widget, widgetName, publicUpload } = props;
  const options = getWidgetProps(props);

  const { fieldName, filenameFieldName } = useUploadWidgetFieldName({
    publicUpload,
    options,
  });

  const fileId = useWatch({ name: fieldName });
  const filename = useWatch({ name: filenameFieldName });

  const title = useUploadWidgetTitle(widget, publicUpload);
  const label = publicUpload ? widget.publicUploadLabel : options.label;
  const helpText = useUploadWidgetHelpText({
    widget,
    options,
    publicUpload,
  });

  const isButton = widget.kind === UploadWidgetKind.Button;

  const { submissionId, submissionModuleId } = useFillableSubmissionContext();
  const sectionId = getSectionIdFromFieldName(widgetName);

  return (
    <ConfigFormItem
      name={widgetName}
      label={<RichTextAdvancedRender text={label} />}
      help={isButton && <RichTextAdvancedRender text={helpText} />}
      layout={{ hideVisualLabel: options.hideLabel }}
      readonly={options.readonly}
      widget={props.widget}
    >
      {isButton ? (
        <UploadInput
          name={widgetName}
          title={title}
          showUploadList={false}
          disabled={true}
          buttonProps={{ disabled: true }}
        />
      ) : (
        <DraggerInput
          name={widgetName}
          title={title}
          showUploadList={false}
          disabled={true}
          info={helpText ? <RichTextAdvancedRender text={helpText} /> : <div />}
        />
      )}
      <UploadWidgetFileList
        publicUpload={publicUpload}
        widget={widget}
        fileId={fileId}
        filename={filename}
        onFileChange={() => {}}
        onFilenameChange={() => {}}
        readonly={true}
        submissionId={submissionId}
        submissionModuleId={submissionModuleId}
        sectionId={sectionId}
      />
    </ConfigFormItem>
  );
}
